import {
  ArrowRight,
  BarChart3,
  Boxes,
  Globe,
  HandCoins,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { MarketingShell } from './MarketingShell';
import { useBrand } from '../../brand/BrandProvider';
import { useCms, cmsText, cmsList } from '../../api/cms';
import { SINGLE_FALLBACKS, LIST_FALLBACKS } from '../../content/fallbacks';

/**
 * Reseller-branded landing — §5.2 / §5.3
 * Reads the host-resolved brand (useBrand, Decision #21) for name/colour/support,
 * so it renders correctly straight from the brand resolved in SessionGate — no
 * separate reseller fetch, no ITCity/Possignin leak on a reseller host. The hero
 * headline/subhead and the primary CTA label are CMS-editable per reseller
 * (landing.hero / landing.cta) — when unset, the fallback is byte-identical to the
 * brand-interpolated copy this page has always shown; a CMS value, once set, is
 * used verbatim (no further brand interpolation).
 *
 * Chrome (header/nav/footer/theme toggle) comes from MarketingShell — the SAME
 * shell every other public page uses, so /home and /pricing can never drift.
 */

// Icons stay fixed in code and pair with the CMS-editable `landing.features`
// list items by position (see content/registry.ts note).
const FEATURE_ICONS = [Boxes, HandCoins, BarChart3, Smartphone, Globe, ShieldCheck];

export function Landing() {
  const r = useBrand();
  const accent = r.primaryColor;
  const { data: cms } = useCms();
  const heroEyebrow = cmsText(cms, 'landing.hero', 'eyebrow', SINGLE_FALLBACKS['landing.hero']!.eyebrow!);
  const headlineOverride = cmsText(cms, 'landing.hero', 'headline', '');
  const subhead = cmsText(cms, 'landing.hero', 'subhead', SINGLE_FALLBACKS['landing.hero']!.subhead!);
  const ctaText = cmsText(cms, 'landing.cta', 'text', SINGLE_FALLBACKS['landing.cta']!.text!);
  const heroBadges = cmsList(cms, 'landing.heroBadges', LIST_FALLBACKS['landing.heroBadges']!);
  const featuresHeadline = cmsText(cms, 'landing.featuresIntro', 'headline', SINGLE_FALLBACKS['landing.featuresIntro']!.headline!);
  const features = cmsList(cms, 'landing.features', LIST_FALLBACKS['landing.features']!);
  const ctaStrip = SINGLE_FALLBACKS['landing.ctaStrip']!;

  return (
    <MarketingShell>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="glow-bg absolute inset-0 pointer-events-none" aria-hidden="true" />

        <div className="relative px-4 sm:px-6 py-16 sm:py-24 max-w-6xl mx-auto text-center">
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-6"
            style={{
              backgroundColor: `${accent}1a`,
              color: accent,
            }}
          >
            {heroEyebrow}
          </span>

          {headlineOverride.trim() !== '' ? (
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-[1.05] max-w-4xl mx-auto">
              {headlineOverride}
            </h1>
          ) : (
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-[1.05] max-w-4xl mx-auto">
              Run your shop with {r.name}
              <br />
              <span className="text-zinc-500 dark:text-zinc-500">
                — built for Bangladesh.
              </span>
            </h1>
          )}

          <p className="mt-6 text-base sm:text-lg text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto">
            {subhead}
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium text-white shadow-sm transition hover:opacity-90 w-full sm:w-auto"
              style={{ backgroundColor: accent }}
            >
              {ctaText}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium text-zinc-900 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/60 hover:bg-white dark:hover:bg-zinc-900 transition w-full sm:w-auto backdrop-blur-sm"
            >
              Login
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <ul className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-zinc-600 dark:text-zinc-300">
            {heroBadges.map((b, i) => (
              <Tick key={i}>{b.text}</Tick>
            ))}
          </ul>
        </div>
      </section>

      {/* Features — scroll-mt clears the sticky header when arriving via /#features. */}
      <section
        id="features"
        className="px-4 sm:px-6 py-16 sm:py-20 max-w-6xl mx-auto scroll-mt-20"
      >
        <div className="text-center mb-12">
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Why {r.name}
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {featuresHeadline}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {features.map((f, i) => {
            const Icon = FEATURE_ICONS[i] ?? FEATURE_ICONS[FEATURE_ICONS.length - 1]!;
            return <Feature key={i} icon={Icon} accent={accent} title={f.title as string} body={f.body as string} />;
          })}
        </div>
      </section>

      {/* CTA strip */}
      <section className="px-4 sm:px-6 py-14 max-w-6xl mx-auto">
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {cmsText(cms, 'landing.ctaStrip', 'headline', ctaStrip.headline!)}
          </h2>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 max-w-md mx-auto">
            {cmsText(cms, 'landing.ctaStrip', 'body', ctaStrip.body!)}
          </p>
          <Link
            to="/signup"
            className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium text-white shadow-sm transition hover:opacity-90"
            style={{ backgroundColor: accent }}
          >
            Start free trial
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

    </MarketingShell>
  );
}

function Tick({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
      {children}
    </li>
  );
}

function Feature({
  icon: Icon,
  accent,
  title,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 p-5 sm:p-6 hover:shadow-sm transition">
      <span
        className="w-10 h-10 grid place-items-center rounded-lg text-white"
        style={{ backgroundColor: accent }}
      >
        <Icon className="w-5 h-5" />
      </span>
      <h3 className="mt-4 text-base font-semibold text-zinc-950 dark:text-zinc-50">
        {title}
      </h3>
      <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
        {body}
      </p>
    </div>
  );
}
